// Endscreen Overlay
// Renders the run summary when an ending fires (#ui:endscreen + ending_reached
// VAR). Three actions: Continue (post-ending mode), Main Menu, Restart.
// Ink parks the player at Endgame_PostEnding_Park with 3 keyword-matched
// choices (continue_apartment / continue_loft / return_to_menu); this overlay
// drives whichever one matches the player's chosen route.

import { variableManager } from '../engine/variables.js';
import { events, Events } from '../engine/events.js';
import { L } from '../systems/localization.js';
import { tutorialTips } from './tutorial-tips.js';
import { layoutManager } from './layout.js';

function t(key, fallback) {
    const v = L.get(key);
    return v === key ? fallback : v;
}

const ENDING_DEFS = {
    kept: {
        nameKey: 'endgame.e1.name', nameDefault: 'Kept',
        taglineKey: 'endgame.e1.tagline', taglineDefault: 'Owned. Collared. His.',
        hero: 'endgame_mirror_final',
        continueRoute: 'apartment',  // matches continue_apartment choice
    },
    independent: {
        nameKey: 'endgame.e2.name', nameDefault: 'Free',
        taglineKey: 'endgame.e2.tagline', taglineDefault: 'On my own terms.',
        hero: 'endgame_mirror_independent',
        continueRoute: 'loft',
    },
    famous: {
        nameKey: 'endgame.e3.name', nameDefault: 'Famous',
        taglineKey: 'endgame.e3.tagline', taglineDefault: 'Going live.',
        hero: 'endgame_mirror_famous',
        continueRoute: 'apartment',
    },
    // MASC_ROUTE (0.2.8) 4th ending. continueRoute 'apartment'
    // — the masc PC stays put (does not move out like Independent).
    masc: {
        nameKey: 'endgame.e4.name', nameDefault: 'On My Own Terms, As a Man',
        taglineKey: 'endgame.e4.tagline', taglineDefault: 'A man, on his own terms.',
        hero: 'endgame_mirror_masc',
        continueRoute: 'apartment',
    },
    // Kept By Her (Sarah-Domme, 0.3, v030-gated) 5th ending. A Sarah-domme PC
    // can be at the apartment OR the loft (Independent moved_out), so the
    // continue route accepts BOTH park-exit keywords; the park surfaces only
    // the one matching moved_out.
    kept_her: {
        nameKey: 'endgame.e5.name', nameDefault: 'Hers',
        taglineKey: 'endgame.e5.tagline', taglineDefault: 'Owned. Collared. Hers.',
        hero: 'endgame_mirror_kept_her',
        continueRoute: ['apartment', 'loft'],
    },
};

class Endscreen {
    constructor() {
        this._el = null;
        this._scope = null;
        this._endingKey = null;
    }

    show(endingKey) {
        if (!ENDING_DEFS[endingKey]) {
            console.warn('[Endscreen] Unknown ending key:', endingKey);
            return;
        }
        this._endingKey = endingKey;
        this._ensureDom();
        this._render();
        // Route through layoutManager so the backdrop dims the rest + the
        // overlay-stack tracks this entry. Falls back to bare class toggle
        // if the manager isn't ready yet.
        if (layoutManager && layoutManager.openOverlay) {
            layoutManager.openOverlay('overlay-endscreen', false);
        } else {
            this._el.classList.remove('hidden');
        }
    }

    hide() {
        if (layoutManager && layoutManager.closeOverlay) {
            layoutManager.closeOverlay('overlay-endscreen');
        } else if (this._el) {
            this._el.classList.add('hidden');
        }
    }

    _ensureDom() {
        if (this._el) return;
        const el = document.createElement('div');
        el.id = 'overlay-endscreen';
        el.className = 'overlay overlay-endscreen hidden';
        el.innerHTML = `
            <div class="overlay-box es-box">
                <div class="es-hero"><img class="es-hero-img" alt=""></div>
                <div class="es-body">
                    <div class="es-title-block">
                        <div class="es-tagline"></div>
                        <h2 class="es-title"></h2>
                    </div>
                    <div class="es-summary"></div>
                    <div class="es-thanks">
                        <p>${t('endscreen.thanks_line1', 'Thank you for playing Pinked and Owned.')}</p>
                        <p>${t('endscreen.thanks_line2', 'This is the current ending, not the final one. More content, endings, and maybe even a Chapter 2 are on the way. Your support means everything. 💗')}</p>
                    </div>
                    <div class="es-community">
                        <a href="https://discord.gg/pinkedandowned" target="_blank" rel="noopener" class="es-cta es-cta-discord">
                            <span class="es-cta-icon" aria-hidden="true">💬</span>
                            <span class="es-cta-label">${t('endscreen.discord_cta', 'Share feedback on Discord')}</span>
                        </a>
                        <a href="https://www.patreon.com/PinkedAndOwned" target="_blank" rel="noopener" class="es-cta es-cta-patreon">
                            <span class="es-cta-icon" aria-hidden="true">💗</span>
                            <span class="es-cta-label">${t('endscreen.patreon_cta', 'Support on Patreon')}</span>
                        </a>
                    </div>
                    <div class="es-actions">
                        <button class="es-btn es-btn-continue">${t('endscreen.continue', 'Continue Playing')}</button>
                        <button class="es-btn es-btn-restart">${t('endscreen.restart', 'New Game (with perks!)')}</button>
                        <button class="es-btn es-btn-menu">${t('endscreen.mainmenu', 'Main Menu')}</button>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(el);
        this._el = el;

        this._scope = events.createScope ? events.createScope('endscreen') : null;
        if (this._scope) {
            this._scope.on(Events.LOCALE_CHANGED, () => { if (this._el) this._render(); });
        }
        el.querySelector('.es-btn-continue').addEventListener('click', () => this._continue());
        el.querySelector('.es-btn-menu').addEventListener('click', () => this._mainMenu());
        el.querySelector('.es-btn-restart').addEventListener('click', () => this._restart());
    }

    destroy() {
        if (this._scope) {
            this._scope.destroy();
            this._scope = null;
        }
        if (this._el) {
            this._el.remove();
            this._el = null;
        }
    }

    _read(name) {
        const story = variableManager._story;
        if (story) {
            try {
                const v = story.variablesState[name];
                if (v !== undefined && v !== null) return v;
            } catch (e) { /* fall through */ }
        }
        return variableManager.getValue(name) || 0;
    }

    _render() {
        const def = ENDING_DEFS[this._endingKey];
        const name = t(def.nameKey, def.nameDefault);
        const tagline = t(def.taglineKey, def.taglineDefault);

        const heroImg = this._el.querySelector('.es-hero-img');
        heroImg.src = this._resolveHero(def.hero);
        heroImg.alt = name;

        this._el.querySelector('.es-title').textContent = name;
        this._el.querySelector('.es-tagline').textContent = tagline;

        const v = {
            Day: this._read('Day'),
            Femininity: this._read('Femininity'),
            Submissiveness: this._read('Submissiveness'),
            Money: this._read('Money'),
            MasterRelationship: this._read('MasterRelationship'),
            sissy_followers: this._read('sissy_followers'),
            sissy_fame: this._read('sissy_fame'),
            onlyfems_subscribers: this._read('onlyfems_subscribers'),
            sarah_relationship: this._read('sarah_relationship'),
        };

        const rows = this._summaryRows(v);
        this._el.querySelector('.es-summary').innerHTML = rows.map(r =>
            `<div class="es-stat-row"><span class="es-stat-label">${r.label}</span><span class="es-stat-value">${r.value}</span></div>`
        ).join('');
    }

    _resolveHero(baseName) {
        // 2-arg form returns .png (dev), or blob URL (tar mode, production).
        // 1-arg form uses .webp via manifest (production builds convert).
        try {
            if (typeof window !== 'undefined' && window._pno_resolveImage) {
                // Try 2-arg .png path first — works on dev server where files
                // are raw PNG. Production tar mode handles its own routing.
                const png = window._pno_resolveImage('Endgame', baseName);
                if (png) return png;
            }
        } catch (e) { /* ignore */ }
        return `assets/images/Endgame/${baseName}.png`;
    }

    _summaryRows(v) {
        const rows = [
            { label: t('endscreen.summary.day', 'Days lived'), value: v.Day },
            { label: t('endscreen.summary.fem', 'Femininity'), value: v.Femininity },
            { label: t('endscreen.summary.sub', 'Submissiveness'), value: v.Submissiveness },
        ];
        if (this._endingKey === 'kept') {
            rows.push({ label: t('endscreen.summary.master_rel', 'Master bond'), value: v.MasterRelationship });
        } else if (this._endingKey === 'famous') {
            rows.push({ label: t('endscreen.summary.followers', 'Followers'), value: v.sissy_followers });
            rows.push({ label: t('endscreen.summary.subs', 'OnlyFems subs'), value: v.onlyfems_subscribers });
            rows.push({ label: t('endscreen.summary.fame', 'Fame'), value: v.sissy_fame });
        } else if (this._endingKey === 'independent') {
            rows.push({ label: t('endscreen.summary.money', 'Money'), value: `$${v.Money}` });
        } else if (this._endingKey === 'masc') {
            rows.push({ label: t('endscreen.summary.money', 'Money'), value: `$${v.Money}` });
        } else if (this._endingKey === 'kept_her') {
            rows.push({ label: t('endscreen.summary.her_devotion', 'Her devotion'), value: v.sarah_relationship });
        }
        return rows;
    }

    _continue() {
        const def = ENDING_DEFS[this._endingKey];
        this.hide();
        // Trigger the Ink parking-knot choice that matches the player's route.
        // Ink labels are "Walk back to the apartment" / "Walk back to the loft";
        // substring-match on 'apartment' / 'loft' is enough. continueRoute may be
        // a single keyword (string) or an array (kept_her: apartment OR loft).
        const routes = Array.isArray(def.continueRoute) ? def.continueRoute : [def.continueRoute];
        tutorialTips.autoSelectExitChoice(routes);
    }

    _mainMenu() {
        this.hide();
        // RELOAD, don't SCREEN_CHANGE. The main-menu + character-editor DOM are
        // unmounted the first time the player enters the game view (layout.js
        // showScreen, "Going back to either uses location.reload()"). By the
        // time any ending fires that unmount has happened, so an in-memory
        // SCREEN_CHANGE paints an empty black menu. Reload rebuilds the DOM —
        // same fix the pause-menu Quit uses (main.js). foundby:Monki 0.2.8.6.
        // (Supersedes the foundby:Krama 0.2.8 SCREEN_CHANGE approach, which
        // only avoided a mis-clicked auto-select but still landed on dead DOM.)
        location.reload();
    }

    _restart() {
        const ok = window.confirm(t('endscreen.restart_confirm',
            'Start a fresh run? Your current save will be replaced.'));
        if (!ok) return;
        this.hide();
        // Same unmounted-DOM problem as _mainMenu: clicking New Game in-memory
        // hit a null #btn-new-game (silent no-op) on an empty black menu →
        // "transitions to black and doesn't actually start a new game"
        // (foundby:Monki 0.2.8.6). Reload to rebuild the DOM, and flag the boot
        // to walk straight into a fresh character editor so the button keeps its
        // promise. Perks carry over because they derive from achievements (the
        // "safe"), which persist across the reload.
        try { sessionStorage.setItem('PnO_BootIntoNewGame', '1'); } catch (e) { /* private mode */ }
        location.reload();
    }
}

export const endscreen = new Endscreen();
if (typeof window !== 'undefined') {
    window._endscreen = endscreen;
}
